import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { publicSupabase } from "@/lib/public-supabase";
import { GoogleReCaptcha } from "@/components/shared/GoogleReCaptcha";
import { validateInquiryForm, defaultRateLimiter } from "@/utils/validation";
import { useUserProfile } from "@/hooks/useUserProfile";

interface InquiryFormProps {
  propertyId: string;
  propertyTitle: string;
}

export const InquiryForm = ({ propertyId, propertyTitle }: InquiryFormProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    client_name: "",
    client_email: "",
    client_phone: "",
    message: ""
  });
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaReady, setCaptchaReady] = useState(false);
  const { toast } = useToast();
  const { profile } = useUserProfile();

  // Auto-fill from profile
  useEffect(() => {
    if (profile) {
      setFormData(prev => ({
        ...prev,
        client_name: prev.client_name || profile.full_name,
        client_email: prev.client_email || profile.email,
        client_phone: prev.client_phone || profile.phone,
      }));
    }
  }, [profile]);

  useEffect(() => {
    if (captchaToken) {
      const timer = setTimeout(() => {
        setCaptchaToken(null);
        setCaptchaReady(false);
      }, 110000);
      return () => clearTimeout(timer);
    }
  }, [captchaToken]);

  const handleCaptchaVerify = (token: string) => {
    setCaptchaToken(token);
    setCaptchaReady(true);
  };

  const handleCaptchaError = () => {
    setCaptchaToken(null);
    setCaptchaReady(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const clientIP = 'user';
      if (!defaultRateLimiter.isAllowed(clientIP)) {
        toast({
          title: "Too Many Requests",
          description: "Please wait a moment before submitting another inquiry.",
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }

      const validation = validateInquiryForm(formData);
      if (!validation.isValid) {
        toast({
          title: "Validation Error",
          description: validation.errors.join(', '),
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }

      if (captchaToken) {
        try {
          const { data: recaptchaRes, error: recaptchaError } = await publicSupabase.functions.invoke('verify-recaptcha', {
            body: { token: captchaToken, expectedAction: 'inquiry_submit' }
          });

          if (recaptchaError || !recaptchaRes?.success) {
            console.warn('reCAPTCHA verification failed, proceeding anyway');
          }
        } catch (captchaErr) {
          console.warn('reCAPTCHA check failed:', captchaErr);
        }
      }

      const { data: property, error: propertyError } = await publicSupabase
        .from("properties")
        .select("title, locality, city, price")
        .eq("id", propertyId)
        .maybeSingle();

      if (propertyError) {
        console.error('Error fetching property:', propertyError);
        toast({ title: "Error", description: "Failed to fetch property details.", variant: "destructive" });
        setIsLoading(false);
        return;
      }

      if (!property) {
        toast({ title: "Error", description: "Property not found.", variant: "destructive" });
        setIsLoading(false);
        return;
      }

      // Ensure phone meets RLS minimum length (10 chars)
      let phone = (validation.data.client_phone ?? '').replace(/[^0-9+\-\s()]/g, '').trim();
      if (phone.length < 10 && !phone.startsWith('+91')) {
        phone = '+91' + phone;
      }

      const { error } = await publicSupabase
        .from("inquiries")
        .insert({
          property_id: propertyId,
          name: validation.data.client_name,
          email: validation.data.client_email || null,
          phone: phone,
          message: validation.data.message || `Inquiry for: ${propertyTitle}`
        });

      if (error) throw error;

      try {
        const formatPrice = (price: number | null) => {
          if (!price) return 'Price on request';
          if (price >= 10000000) return `₹${(price / 10000000).toFixed(1)}Cr`;
          if (price >= 100000) return `₹${(price / 100000).toFixed(1)}L`;
          return `₹${price.toLocaleString()}`;
        };

        await publicSupabase.functions.invoke('send-inquiry-notification', {
          body: {
            propertyTitle: property.title,
            clientName: validation.data.client_name,
            clientEmail: validation.data.client_email,
            clientPhone: validation.data.client_phone,
            message: validation.data.message,
            propertyLocation: `${property.locality || ''}, ${property.city || ''}`.replace(/^, |, $/g, ''),
            propertyPrice: formatPrice(property.price)
          }
        });

        await publicSupabase.functions.invoke('send-notification', {
          body: {
            type: 'inquiry',
            recipientEmail: 'admin@example.com',
            data: {
              name: validation.data.client_name,
              phone: validation.data.client_phone,
              email: validation.data.client_email,
              propertyTitle: property.title,
              message: validation.data.message,
            }
          }
        });
      } catch (emailError) {
        console.warn("Email notification failed:", emailError);
      }

      toast({
        title: "Inquiry Submitted",
        description: "Thank you for your interest! We'll contact you soon.",
      });

      setFormData({
        client_name: "",
        client_email: "",
        client_phone: "",
        message: ""
      });
      setCaptchaToken(null);
      setCaptchaReady(false);
    } catch (error) {
      console.error("Error submitting inquiry:", error);
      toast({
        title: "Error",
        description: "Failed to submit inquiry. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <GoogleReCaptcha
        onVerify={handleCaptchaVerify}
        onError={handleCaptchaError}
        action="inquiry_submit"
      />

      <div className="space-y-2">
        <Label htmlFor="name">Full Name *</Label>
        <Input
          id="name"
          value={formData.client_name}
          onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
          required
          maxLength={50}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="email">Email *</Label>
        <Input
          id="email"
          type="email"
          value={formData.client_email}
          onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
          required
          maxLength={100}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="phone">Phone Number *</Label>
        <Input
          id="phone"
          type="tel"
          value={formData.client_phone}
          onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
          required
          maxLength={20}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="message">Message (Optional)</Label>
        <Textarea
          id="message"
          placeholder={`I'm interested in ${propertyTitle}...`}
          value={formData.message}
          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
          rows={3}
          maxLength={2000}
        />
      </div>

      <Button type="submit" className="w-full" disabled={isLoading}>
        {isLoading ? "Submitting..." : "Submit Inquiry"}
      </Button>
    </form>
  );
};
